import React, { useState } from 'react';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';

/**
 * AI Metric Assistant
 * 
 * A conversational interface that helps users create business metrics
 * by describing what they want in natural language.
 */
const AIMetricAssistant = ({ companyId, onMetricGenerated, onClose }) => {
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedConfig, setGeneratedConfig] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [error, setError] = useState(null);

  // Example prompts to help users get started
  const examplePrompts = [
    "Total revenue from all orders",
    "Customer churn rate - percentage of customers who cancelled",
    "Average order value across all transactions",
    "Number of new customers acquired this month",
    "Appointment completion rate by doctor"
  ];

  // Category colors
  const categoryColors = {
    general: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    revenue: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    customers: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    operations: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    performance: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    growth: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400'
  };

  const handleGenerate = async () => {
    if (!description.trim()) {
      toast.error('Please describe the metric you want to create');
      return;
    }

    setIsLoading(true);
    setError(null);
    setGeneratedConfig(null);

    try {
      const response = await apiHelpers.generateMetricWithAI({
        description: description.trim(),
        companyId
      });

      if (response.data.success) {
        setGeneratedConfig(response.data.metricConfig);
        setExplanation(response.data.explanation || response.data.metricConfig.explanation);
        toast.success('Metric configuration generated!');
      } else {
        setError(response.data.error || 'Failed to generate metric configuration');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      setError(err.response?.data?.error || 'Failed to generate metric configuration');
      toast.error('Failed to generate metric');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseMetric = () => {
    if (generatedConfig && onMetricGenerated) {
      // Remove the explanation field and format for the metric form
      const { explanation: _, ...metricData } = generatedConfig;
      
      // Format aiHints.synonyms as comma-separated string for the form
      const formattedData = {
        ...metricData,
        aiHints: {
          ...metricData.aiHints,
          synonyms: metricData.aiHints?.synonyms?.join(', ') || ''
        }
      };
      
      onMetricGenerated(formattedData);
    }
  };

  const handleReset = () => {
    setDescription('');
    setGeneratedConfig(null);
    setExplanation('');
    setError(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-blue-600 to-cyan-600">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">AI Metric Assistant</h2>
              <p className="text-sm text-white/80">Describe the metric you want to track</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/20 text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Input Section */}
          {!generatedConfig && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Describe your metric
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="E.g., Total revenue from completed orders, or Customer retention rate over the past year..."
                  rows={4}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  disabled={isLoading}
                />
              </div>

              {/* Example Prompts */}
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Try these examples:</p>
                <div className="flex flex-wrap gap-2">
                  {examplePrompts.map((prompt, index) => (
                    <button
                      key={index}
                      onClick={() => setDescription(prompt)}
                      className="text-xs px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                    >
                      {prompt.length > 40 ? prompt.substring(0, 40) + '...' : prompt}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                </div>
              )}
            </>
          )}

          {/* Generated Config Preview */}
          {generatedConfig && (
            <div className="space-y-4">
              {/* Explanation */}
              {explanation && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-1">AI Explanation</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-400">{explanation}</p>
                </div>
              )}

              {/* Metric Details */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded mb-2 ${categoryColors[generatedConfig.category] || categoryColors.general}`}>
                      {generatedConfig.category}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {generatedConfig.displayName}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                      {generatedConfig.name}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{generatedConfig.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Source Model</label>
                    <p className="text-sm font-mono text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                      {generatedConfig.sourceModel}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Unit</label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {generatedConfig.aiHints?.unitLabel || generatedConfig.aiHints?.unit || 'count'}
                    </p>
                  </div>
                </div>

                {/* Measure Definition */}
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Measure Definition</label>
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Type:</span>
                      <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-sm">
                        {generatedConfig.measure?.type}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Format:</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {generatedConfig.measure?.format}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">SQL Expression:</span>
                      <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded block text-green-600 dark:text-green-400">
                        {generatedConfig.measure?.sql}
                      </code>
                    </div>
                  </div>
                </div>

                {/* AI Hints */}
                {generatedConfig.aiHints && (
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">AI Hints</label>
                    
                    {generatedConfig.aiHints.synonyms?.length > 0 && (
                      <div className="mb-3">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Synonyms:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {generatedConfig.aiHints.synonyms.map((syn, i) => (
                            <span key={i} className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-xs">
                              {syn}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {generatedConfig.aiHints.whenToUse && (
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">When to use:</span>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                          {generatedConfig.aiHints.whenToUse}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
          {!generatedConfig ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={isLoading || !description.trim()}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                {isLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Generate Metric
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Try Again
              </button>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUseMetric}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Use This Metric
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIMetricAssistant;

